import { characterPlace } from "./xiezi-copy.js";
import {
  type DrillProgress,
  hidesCharacter,
  passesPerStage,
  stages,
} from "../handwriting/xiezi-stages.js";

/**
 * The row of characters over an Exercise's square, saying where the reader is
 * with each.
 *
 * `partials/lianxi/drill.html` draws one item per character, each with a place
 * inside it marked `data-cb-xiezi-character-place`, and this writes each
 * character's stage into its place and marks the item for the character in the
 * square. `lianxi-xiezi.ts` calls it after every move between characters.
 */

/**
 * Say where each character is.
 * @param items - The row's items, one per character, in the Exercise's order.
 * @param progress - Where the reader is with each character, in the same order.
 * @param current - Which character is in the square, or nothing once every
 * character is finished.
 */
export function showPlaces(
  items: readonly HTMLElement[],
  progress: readonly DrillProgress[],
  current: number | undefined,
): void {
  items.forEach((item, at) => {
    const held = progress[at];
    const place = item.querySelector("[data-cb-xiezi-character-place]");

    if (held !== undefined && place !== null) {
      place.textContent = characterPlace(held);
      const completed = Math.min(
        stages.length * passesPerStage,
        held.stage * passesPerStage + held.pass,
      );
      const previous = item.dataset["cbXieziCompleted"];
      item.dataset["cbXieziCompleted"] = String(completed);
      item.style.setProperty(
        "--cb-xiezi-completion",
        `${String((completed / (stages.length * passesPerStage)) * 100)}%`,
      );
      if (
        previous !== undefined &&
        completed > Number(previous) &&
        item.ownerDocument.defaultView?.matchMedia?.(
          "(prefers-reduced-motion: reduce)",
        ).matches === false
      ) {
        item.animate?.(
          [
            { transform: "scale(1)" },
            {
              transform:
                completed % passesPerStage === 0
                  ? "scale(1.08)"
                  : "scale(1.03)",
            },
            { transform: "scale(1)" },
          ],
          { duration: 450, easing: "ease-out" },
        );
      }
    }

    // A character being written from memory shows its reading in the row
    // rather than itself. `modules/lianxi` swaps the two.
    item.toggleAttribute(
      "data-cb-xiezi-recall",
      held !== undefined && hidesCharacter(held),
    );

    if (at === current) {
      item.setAttribute("aria-current", "true");
    } else {
      item.removeAttribute("aria-current");
    }
  });
}
